import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Clipboard,
  Linking,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Carousel from 'react-native-reanimated-carousel';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import * as Sentry from '@sentry/react-native';
import { usePostHog } from 'posthog-js/react';
import { COLORS } from '../../styles/colors';
import { useResponsive } from '../../hooks/useResponsive';
import { useListingDetail } from '../../hooks/useListingDetail';
import FallbackImage from '../common/FallbackImage';
import { useListingsFeedContext } from '../../context/ListingsFeedContext';
import { apiService } from '../../services/apiService';
import logger from '../../utils/logger';
import FinanceLeadForm from './FinanceLeadForm';
import { getSignalColor } from '../../config/listingsConfig';
import OverallMatchBadge from '../feed/OverallMatchBadge';

/** Human-readable labels for score signal names. */
const SIGNAL_LABELS = {
  price_vs_market: 'Price vs Market',
  trim_match: 'Trim Match',
  distance: 'Distance',
  mileage: 'Mileage',
  dom: 'Days on Market',
  year_match: 'Year Match',
  price_trend: 'Price Trend',
  transmission_match: 'Transmission',
  fuel_match: 'Fuel Type',
  owners: 'Previous Owners',
  ulez: 'ULEZ Compliance',
};

/** Icons for each signal. */
const SIGNAL_ICONS = {
  price_vs_market: 'cash-multiple',
  trim_match: 'car-cog',
  distance: 'map-marker-distance',
  mileage: 'speedometer',
  dom: 'calendar-clock',
  year_match: 'calendar-check',
  price_trend: 'trending-down',
  transmission_match: 'car-shift-pattern',
  fuel_match: 'gas-station',
  owners: 'account-group',
  ulez: 'leaf',
};

const SIMILAR_LISTINGS_COUNT = 3;

// ============================================================
// Utility functions
// ============================================================

const formatPrice = (price) => {
  if (price == null || isNaN(price)) return '--';
  return `\u00A3${Number(price).toLocaleString('en-GB')}`;
};

const formatMileage = (miles) => {
  if (miles == null || isNaN(miles)) return '--';
  return `${Number(miles).toLocaleString('en-GB')} mi`;
};

// ============================================================
// Sub-components
// ============================================================

/** Score breakdown: individual signal row. */
const SignalRow = memo(({ signal, isMobile }) => {
  const color = getSignalColor(signal.value);
  const label = SIGNAL_LABELS[signal.signal] || signal.signal;
  const icon = SIGNAL_ICONS[signal.signal] || 'information-outline';
  return (
    <View style={[signalStyles.row, isMobile && signalStyles.rowMobile]}>
      <View style={signalStyles.iconContainer}>
        <Icon name={icon} size={16} color={color} />
      </View>
      <View style={signalStyles.content}>
        <View style={signalStyles.labelRow}>
          <Text style={signalStyles.label}>{label}</Text>
        </View>
        <View style={signalStyles.barContainer}>
          <View style={[signalStyles.bar, { width: `${signal.value}%`, backgroundColor: color }]} />
        </View>
        <Text style={signalStyles.explanation}>{signal.explanation}</Text>
      </View>
      <View style={[signalStyles.valueBadge, { backgroundColor: `${color}15` }]}>
        <Text style={[signalStyles.valueText, { color }]}>{Math.round(signal.value)}</Text>
      </View>
    </View>
  );
});
SignalRow.displayName = 'SignalRow';

const signalStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.borderColor,
    gap: 10,
  },
  rowMobile: {
    paddingVertical: 8,
  },
  iconContainer: {
    width: 28,
    height: 28,
    borderRadius: 6,
    backgroundColor: COLORS.lightGray,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 2,
  },
  content: {
    flex: 1,
  },
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  label: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 13,
    color: COLORS.textPrimary,
  },
  barContainer: {
    height: 4,
    backgroundColor: COLORS.lightGray,
    borderRadius: 2,
    marginBottom: 4,
    overflow: 'hidden',
  },
  bar: {
    height: 4,
    borderRadius: 2,
  },
  explanation: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 12,
    color: COLORS.textSecondary,
    lineHeight: 16,
  },
  valueBadge: {
    width: 32,
    height: 24,
    borderRadius: 6,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 2,
  },
  valueText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 12,
  },
});

/** Photo gallery using react-native-reanimated-carousel (same as product page). */
const PhotoGallery = memo(({ photos, modelImageUrl, isUnavailable, isMobile }) => {
  const carouselRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);

  const images = useMemo(() => {
    if (photos && photos.length > 0) return photos;
    if (modelImageUrl) return [modelImageUrl];
    return [];
  }, [photos, modelImageUrl]);

  const imageHeight = isMobile ? 220 : 320;

  if (images.length === 0) {
    return (
      <View style={[galleryStyles.placeholder, { height: imageHeight }]}>
        <Icon name="car" size={56} color={COLORS.mediumGray} />
        <Text style={galleryStyles.placeholderText}>No photos available</Text>
      </View>
    );
  }

  return (
    <View
      style={{ height: imageHeight }}
      onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}
    >
      {containerWidth > 0 ? (
        <Carousel
          ref={carouselRef}
          loop={images.length > 1}
          width={containerWidth}
          height={imageHeight}
          autoPlay={false}
          data={images}
          scrollAnimationDuration={300}
          onSnapToItem={setActiveIndex}
          renderItem={({ item }) => (
            <FallbackImage
              primitive="rn"
              dealerPhotoUrl={item}
              modelImageUrl={modelImageUrl}
              style={[galleryStyles.image, { height: imageHeight, width: containerWidth }]}
              resizeMode="cover"
            />
          )}
          onConfigurePanGesture={(gesture) => {
            if (gesture.config) gesture.config.touchAction = 'pan-y';
            gesture.activeOffsetX([-10, 10]);
            gesture.failOffsetY([-5, 5]);
          }}
        />
      ) : (
        <FallbackImage
          primitive="rn"
          dealerPhotoUrl={images[0]}
          modelImageUrl={modelImageUrl}
          style={[galleryStyles.image, { height: imageHeight }]}
          resizeMode="cover"
        />
      )}

      {/* Arrow navigation */}
      {images.length > 1 && containerWidth > 0 && (
        <>
          <TouchableOpacity
            style={[galleryStyles.arrow, galleryStyles.arrowLeft]}
            onPress={() => carouselRef.current?.prev()}
          >
            <Icon name="chevron-left" size={28} color="#000" />
          </TouchableOpacity>
          <TouchableOpacity
            style={[galleryStyles.arrow, galleryStyles.arrowRight]}
            onPress={() => carouselRef.current?.next()}
          >
            <Icon name="chevron-right" size={28} color="#000" />
          </TouchableOpacity>
        </>
      )}

      {/* Sold overlay */}
      {isUnavailable && (
        <View style={galleryStyles.unavailableOverlay}>
          <View style={galleryStyles.unavailableChip}>
            <Icon name="car-off" size={16} color={COLORS.white} />
            <Text style={galleryStyles.unavailableText}>No Longer Available</Text>
          </View>
        </View>
      )}

      {/* Photo counter */}
      {images.length > 1 && (
        <View style={galleryStyles.counter}>
          <Text style={galleryStyles.counterText}>
            {activeIndex + 1} / {images.length}
          </Text>
        </View>
      )}
    </View>
  );
});
PhotoGallery.displayName = 'PhotoGallery';

const galleryStyles = StyleSheet.create({
  placeholder: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: COLORS.lightGray,
  },
  placeholderText: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 14,
    color: COLORS.textTertiary,
    marginTop: 8,
  },
  image: {
    backgroundColor: COLORS.lightGray,
  },
  arrow: {
    position: 'absolute',
    top: '50%',
    transform: [{ translateY: -20 }],
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.75)',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.15)',
  },
  arrowLeft: {
    left: 12,
  },
  arrowRight: {
    right: 12,
  },
  unavailableOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  unavailableChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
  },
  unavailableText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 13,
    color: COLORS.white,
  },
  counter: {
    position: 'absolute',
    top: 12,
    left: 12,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  counterText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 12,
    color: COLORS.white,
  },
});

/** Section header used throughout the popup. */
const SectionHeader = memo(({ icon, title, isMobile }) => (
  <View style={sectionStyles.header}>
    <Icon name={icon} size={isMobile ? 18 : 20} color={COLORS.primary} />
    <Text style={[sectionStyles.title, isMobile && sectionStyles.titleMobile]}>{title}</Text>
  </View>
));
SectionHeader.displayName = 'SectionHeader';

const sectionStyles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
    marginTop: 24,
  },
  title: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 17,
    color: COLORS.textPrimary,
    lineHeight: 24,
  },
  titleMobile: {
    fontSize: 15,
  },
});

/** Specification row: label + value. */
const SpecRow = memo(({ label, value, icon }) => {
  if (value == null || value === '' || value === '--') return null;
  return (
    <View style={specStyles.row}>
      {icon && <Icon name={icon} size={14} color={COLORS.textTertiary} style={specStyles.icon} />}
      <Text style={specStyles.label}>{label}</Text>
      <Text style={specStyles.value}>{value}</Text>
    </View>
  );
});
SpecRow.displayName = 'SpecRow';

const specStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.borderColor,
  },
  icon: {
    marginRight: 8,
    width: 16,
  },
  label: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 13,
    color: COLORS.textSecondary,
    flex: 1,
  },
  value: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 13,
    color: COLORS.textPrimary,
    textAlign: 'right',
    flexShrink: 0,
    maxWidth: '60%',
  },
});

/** Feature chip displayed in the features section. */
const FeatureChip = memo(({ text, isHighValue }) => (
  <View style={[featureStyles.chip, isHighValue && featureStyles.chipHighValue]}>
    {isHighValue && <Icon name="star" size={12} color={COLORS.primary} />}
    <Text style={[featureStyles.text, isHighValue && featureStyles.textHighValue]}>{text}</Text>
  </View>
));
FeatureChip.displayName = 'FeatureChip';

const featureStyles = StyleSheet.create({
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: COLORS.lightGray,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.borderColor,
  },
  chipHighValue: {
    backgroundColor: 'rgba(20, 76, 184, 0.06)',
    borderColor: 'rgba(20, 76, 184, 0.15)',
  },
  text: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 12,
    color: COLORS.textSecondary,
  },
  textHighValue: {
    fontFamily: 'Satoshi-Medium',
    color: COLORS.primary,
  },
});

/** Mini listing card for "Similar Listings" section. */
const SimilarListingCard = memo(({ listing, onPress }) => {
  const snapshot = listing?.listing_snapshot || {};

  const handlePress = useCallback(() => {
    if (onPress) onPress(listing);
  }, [onPress, listing]);

  return (
    <Pressable style={similarStyles.card} onPress={handlePress} accessibilityRole="button">
      {(snapshot.photo_url || listing?.modelImageUrl) ? (
        <FallbackImage
          primitive="rn"
          dealerPhotoUrl={snapshot.photo_url}
          modelImageUrl={listing?.modelImageUrl || null}
          style={similarStyles.image}
          resizeMode="cover"
        />
      ) : (
        <View style={similarStyles.imagePlaceholder}>
          <Icon name="car" size={24} color={COLORS.mediumGray} />
        </View>
      )}
      <View style={similarStyles.content}>
        <Text style={similarStyles.title} numberOfLines={1}>
          {snapshot.year} {snapshot.make} {snapshot.model}
        </Text>
        <OverallMatchBadge listing={listing} />
        <Text style={similarStyles.price}>{formatPrice(snapshot.price)}</Text>
      </View>
    </Pressable>
  );
});
SimilarListingCard.displayName = 'SimilarListingCard';

const similarStyles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    backgroundColor: COLORS.lightGray,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: COLORS.borderColor,
    marginBottom: 8,
  },
  image: {
    width: 80,
    height: 72,
    backgroundColor: COLORS.lightGray,
  },
  imagePlaceholder: {
    width: 80,
    height: 72,
    backgroundColor: COLORS.lightGray,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flex: 1,
    padding: 8,
    justifyContent: 'center',
  },
  title: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 13,
    color: COLORS.textPrimary,
    marginBottom: 4,
  },
  scoreBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    marginBottom: 4,
  },
  scoreText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 11,
  },
  price: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 14,
    color: COLORS.textPrimary,
  },
});

// ============================================================
// Main component
// ============================================================

/**
 * ListingDetailPopup - Full listing detail modal.
 *
 * Shows score breakdown, photo gallery, vehicle specifications,
 * features, similar listings, finance CTA, and share button.
 *
 * Lazy-loads full listing details via useListingDetail only when open.
 *
 * @param {Object}   props
 * @param {Object}   props.listing         - Feed entry from user_listing_feeds
 * @param {boolean}  props.visible         - Modal visibility
 * @param {Function} props.onClose         - Close handler
 * @param {Function} [props.onFinanceClick] - Finance CTA handler (receives listing)
 * @param {Function} [props.onSimilarPress] - Handler when a similar listing is tapped
 */
const ListingDetailPopup = ({
  listing,
  visible,
  onClose,
  onFinanceClick,
  onSimilarPress,
}) => {
  const { isMobile } = useResponsive();
  const posthog = usePostHog();
  const { listings, handleView } = useListingsFeedContext();
  const scrollViewRef = useRef(null);

  // Share state
  const [isSharing, setIsSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [showFinanceForm, setShowFinanceForm] = useState(false);

  // Feed entry data
  const feedEntry = listing || {};
  const snapshot = feedEntry.listing_snapshot || {};
  const score = feedEntry.score ?? 0;
  const scoreBreakdown = feedEntry.score_breakdown || [];
  const isUnavailable = feedEntry.status === 'sold' || feedEntry.status === 'removed';

  // Lazy-load full details by feed_entry_id (stable identity).
  const { detail, isLoading: isDetailLoading, error: detailError } = useListingDetail(
    feedEntry.id || null,
    visible
  );

  // Record view interaction when popup opens
  const hasRecordedView = useRef(false);
  useEffect(() => {
    if (visible && feedEntry.id && !hasRecordedView.current) {
      hasRecordedView.current = true;
      handleView(feedEntry.id);
      posthog?.capture('listing_viewed', {
        listing_id: feedEntry.id,
        score: score,
        model_id: feedEntry.model_id,
      });
    }
    if (!visible) {
      hasRecordedView.current = false;
      setShareUrl(null);
      setShareCopied(false);
      setShowFinanceForm(false);
    }
  }, [visible, feedEntry.id, handleView]);

  // Scroll to top when the displayed listing changes (e.g. similar listing tap)
  const prevListingId = useRef(feedEntry.id);
  useEffect(() => {
    if (feedEntry.id && feedEntry.id !== prevListingId.current) {
      prevListingId.current = feedEntry.id;
      scrollViewRef.current?.scrollTo({ y: 0, animated: false });
      // Reset share/finance state for new listing
      setShareUrl(null);
      setShareCopied(false);
      setShowFinanceForm(false);
    }
  }, [feedEntry.id]);

  // Prev/next listing navigation within popup
  const currentIndex = useMemo(() => {
    if (!listings || !feedEntry.id) return -1;
    return listings.findIndex((l) => l.id === feedEntry.id);
  }, [listings, feedEntry.id]);

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < (listings?.length ?? 0) - 1;

  const handlePrevListing = useCallback(() => {
    if (hasPrev && onSimilarPress) {
      onSimilarPress(listings[currentIndex - 1]);
    }
  }, [hasPrev, currentIndex, listings, onSimilarPress]);

  const handleNextListing = useCallback(() => {
    if (hasNext && onSimilarPress) {
      onSimilarPress(listings[currentIndex + 1]);
    }
  }, [hasNext, currentIndex, listings, onSimilarPress]);

  // ---------- Derived data from detail ----------

  const photoLinks = detail?.media?.photo_links || detail?.photo_links || snapshot?.photo_links || [];
  const features = detail?.extra?.features || [];
  const highValueFeatures = detail?.extra?.high_value_features || [];

  // Specs from detail or snapshot fallback
  const specs = useMemo(() => {
    if (!detail) return {};
    const build = detail.build || detail;
    return {
      engine: build.engine || detail.engine,
      fuel: build.fuel_type || detail.fuel_type || snapshot.fuel_type,
      bodyType: build.body_type || detail.body_type,
      drivetrain: build.drivetrain || detail.drivetrain,
      doors: build.doors || detail.doors,
      bhp: build.horsepower || detail.horsepower,
      torque: build.torque || detail.torque,
      zeroToSixty: build.highway_fuel_efficiency || detail['0_to_60'] || detail.zero_to_sixty,
      maxSpeed: build.max_speed || detail.max_speed,
      transmission: build.transmission || detail.transmission || snapshot.transmission,
      // Efficiency
      mpg: build.combined_mpg || detail.combined_mpg || detail.mpg,
      co2: build.co2_emissions || detail.co2_emissions || detail.co2,
      // Condition / History
      miles: detail.miles || snapshot.miles,
      owners: detail.owners || detail.number_of_owners,
      registrationDate: detail.first_registration_date || detail.registration_date,
      writeOffCategory: detail.write_off_category || detail.cat_status,
      ulezCompliant: detail.ulez_compliant,
      euroStatus: detail.euro_status,
      insuranceGroup: detail.insurance_group,
      // EV
      evRange: detail.ev_range || build.ev_range,
      evChargeTime: detail.charge_time || build.charge_time,
    };
  }, [detail, snapshot]);

  // Similar listings: 2-3 from same model, sorted by score desc, excluding current
  const similarListings = useMemo(() => {
    if (!listings || !feedEntry.model_id) return [];
    return listings
      .filter(
        (l) =>
          l.model_id === feedEntry.model_id &&
          l.id !== feedEntry.id &&
          l.status !== 'sold' &&
          l.status !== 'removed'
      )
      .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
      .slice(0, SIMILAR_LISTINGS_COUNT);
  }, [listings, feedEntry.model_id, feedEntry.id]);

  // Is EV?
  const isEV = useMemo(() => {
    const fuel = (specs.fuel || snapshot.fuel_type || '').toLowerCase();
    return fuel.includes('electric') || specs.evRange != null;
  }, [specs.fuel, snapshot.fuel_type, specs.evRange]);

  // ---------- Handlers ----------

  const handleShare = useCallback(async () => {
    if (!feedEntry.id) return;
    setIsSharing(true);
    try {
      const result = await apiService.createListingSnapshot(feedEntry.id);
      const url = result.share_url || result.url;
      setShareUrl(url);
      if (url) {
        Clipboard.setString(url);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 3000);
        posthog?.capture('listing_shared', {
          listing_id: feedEntry.id,
          model_id: feedEntry.model_id,
        });
      }
    } catch (err) {
      logger.error('[ListingDetailPopup] Error creating share snapshot:', err);
      Sentry.captureException(err, {
        tags: { component: 'ListingDetailPopup', operation: 'share' },
      });
    } finally {
      setIsSharing(false);
    }
  }, [feedEntry.id]);

  const handleFinancePress = useCallback(() => {
    setShowFinanceForm(prev => !prev);
  }, []);

  const handleSimilarPress = useCallback(
    (similarListing) => {
      if (onSimilarPress) onSimilarPress(similarListing);
    },
    [onSimilarPress]
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // ---------- Render ----------

  if (!listing) return null;

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={visible}
      onRequestClose={handleClose}
    >
      <View style={styles.overlay}>
        <View style={[styles.modal, isMobile && styles.modalMobile]}>
          {/* Close button */}
          <Pressable
            style={styles.closeButton}
            onPress={handleClose}
            accessibilityRole="button"
            accessibilityLabel="Close listing details"
          >
            <Icon name="close" size={20} color={COLORS.textPrimary} />
          </Pressable>

          <ScrollView
            ref={scrollViewRef}
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            {/* Photo Gallery */}
            <PhotoGallery
              photos={photoLinks}
              modelImageUrl={detail?.modelImageUrl || null}
              isUnavailable={isUnavailable}
              isMobile={isMobile}
            />

            {/* Prev/Next listing navigation */}
            {listings && listings.length > 1 && (
              <View style={styles.listingNavRow}>
                <TouchableOpacity
                  style={[styles.listingNavButton, !hasPrev && styles.listingNavDisabled]}
                  onPress={handlePrevListing}
                  disabled={!hasPrev}
                >
                  <Icon name="chevron-left" size={18} color={hasPrev ? COLORS.textPrimary : COLORS.mediumGray} />
                  <Text style={[styles.listingNavText, !hasPrev && styles.listingNavTextDisabled]}>Prev</Text>
                </TouchableOpacity>
                <Text style={styles.listingNavCounter}>
                  {currentIndex + 1} / {listings.length}
                </Text>
                <TouchableOpacity
                  style={[styles.listingNavButton, !hasNext && styles.listingNavDisabled]}
                  onPress={handleNextListing}
                  disabled={!hasNext}
                >
                  <Text style={[styles.listingNavText, !hasNext && styles.listingNavTextDisabled]}>Next</Text>
                  <Icon name="chevron-right" size={18} color={hasNext ? COLORS.textPrimary : COLORS.mediumGray} />
                </TouchableOpacity>
              </View>
            )}

            <View style={[styles.body, isMobile && styles.bodyMobile]}>
              {/* Title & Price */}
              <View style={styles.headerSection}>
                <Text style={[styles.title, isMobile && styles.titleMobile]}>
                  {snapshot.year} {snapshot.make} {snapshot.model}
                  {snapshot.trim ? ` ${snapshot.trim}` : ''}
                </Text>

                <View style={styles.priceRow}>
                  <Text style={styles.price}>
                    {formatPrice(feedEntry.current_price ?? snapshot.price)}
                  </Text>
                  {feedEntry.price_change_direction && feedEntry.price_change_amount ? (
                    <View
                      style={[
                        styles.priceChangeChip,
                        {
                          backgroundColor:
                            feedEntry.price_change_direction === 'down'
                              ? 'rgba(22,163,74,0.10)'
                              : 'rgba(239,68,68,0.10)',
                        },
                      ]}
                    >
                      <Icon
                        name={
                          feedEntry.price_change_direction === 'down'
                            ? 'arrow-down'
                            : 'arrow-up'
                        }
                        size={14}
                        color={
                          feedEntry.price_change_direction === 'down'
                            ? '#16A34A'
                            : '#EF4444'
                        }
                      />
                      <Text
                        style={[
                          styles.priceChangeAmount,
                          {
                            color:
                              feedEntry.price_change_direction === 'down'
                                ? '#16A34A'
                                : '#EF4444',
                          },
                        ]}
                      >
                        {formatPrice(Math.abs(feedEntry.price_change_amount))}
                      </Text>
                    </View>
                  ) : null}
                </View>

                {/* Quick info row */}
                <View style={styles.quickInfoRow}>
                  <View style={styles.quickInfoItem}>
                    <Icon name="speedometer" size={14} color={COLORS.textTertiary} />
                    <Text style={styles.quickInfoText}>
                      {formatMileage(specs.miles || snapshot.miles)}
                    </Text>
                  </View>
                  {(specs.transmission || snapshot.transmission) && (
                    <View style={styles.quickInfoItem}>
                      <Icon name="car-shift-pattern" size={14} color={COLORS.textTertiary} />
                      <Text style={styles.quickInfoText}>
                        {specs.transmission || snapshot.transmission}
                      </Text>
                    </View>
                  )}
                  {snapshot.city && (
                    <View style={styles.quickInfoItem}>
                      <Icon name="map-marker-outline" size={14} color={COLORS.textTertiary} />
                      <Text style={styles.quickInfoText}>
                        {snapshot.city}
                        {snapshot.distance_miles != null
                          ? ` \u00B7 ${Math.round(snapshot.distance_miles)} mi`
                          : ''}
                      </Text>
                    </View>
                  )}
                </View>
              </View>

              {/* Overall match badge */}
              <View style={styles.scoreSummary}>
                <OverallMatchBadge listing={feedEntry} />
                <Text style={styles.scoreDesc}>
                  Personalised match based on {scoreBreakdown.length || 11} factors
                </Text>
              </View>

              {/* Score Breakdown */}
              <SectionHeader icon="chart-bar" title="Score Breakdown" isMobile={isMobile} />
              {scoreBreakdown.length > 0 ? (
                scoreBreakdown
                  .filter((signal) => {
                    // Hide price_vs_market if the difference is trivial (< £200)
                    if (signal.signal === 'price_vs_market') {
                      const listingPrice = snapshot?.price;
                      const refPrice = snapshot?.ref_price;
                      if (listingPrice && refPrice && Math.abs(listingPrice - refPrice) < 200) {
                        return false;
                      }
                    }
                    return true;
                  })
                  .map((signal, index) => (
                  <SignalRow
                    key={signal.signal || index}
                    signal={signal}
                    isMobile={isMobile}
                  />
                ))
              ) : (
                <Text style={styles.emptyText}>Score breakdown not available</Text>
              )}

              {/* Specifications */}
              <SectionHeader icon="engine" title="Specifications" isMobile={isMobile} />
              {isDetailLoading ? (
                <ActivityIndicator
                  size="small"
                  color={COLORS.primary}
                  style={styles.loader}
                />
              ) : detailError ? (
                <Text style={styles.errorText}>{detailError}</Text>
              ) : (
                <View style={styles.specGrid}>
                  <SpecRow label="Engine" value={specs.engine} icon="engine" />
                  <SpecRow label="Fuel Type" value={specs.fuel} icon="gas-station" />
                  <SpecRow label="Body Type" value={specs.bodyType} icon="car-side" />
                  <SpecRow label="Drivetrain" value={specs.drivetrain} icon="car-traction-control" />
                  <SpecRow label="Doors" value={specs.doors} icon="car-door" />
                  <SpecRow label="BHP" value={specs.bhp ? `${specs.bhp} BHP` : null} icon="flash" />
                  <SpecRow
                    label="Torque"
                    value={specs.torque ? `${specs.torque} Nm` : null}
                    icon="rotate-right"
                  />
                  <SpecRow
                    label="0-60 mph"
                    value={specs.zeroToSixty ? `${specs.zeroToSixty}s` : null}
                    icon="timer"
                  />
                  <SpecRow
                    label="Max Speed"
                    value={specs.maxSpeed ? `${specs.maxSpeed} mph` : null}
                    icon="speedometer"
                  />
                  <SpecRow
                    label="Transmission"
                    value={specs.transmission}
                    icon="car-shift-pattern"
                  />
                </View>
              )}

              {/* Efficiency */}
              {(specs.mpg || specs.co2) && (
                <>
                  <SectionHeader icon="leaf" title="Efficiency" isMobile={isMobile} />
                  <View style={styles.specGrid}>
                    <SpecRow
                      label="Combined MPG"
                      value={specs.mpg ? `${specs.mpg} mpg` : null}
                      icon="gas-station"
                    />
                    <SpecRow
                      label="CO2 Emissions"
                      value={specs.co2 ? `${specs.co2} g/km` : null}
                      icon="molecule-co2"
                    />
                  </View>
                </>
              )}

              {/* Condition & History */}
              <SectionHeader
                icon="clipboard-text-clock"
                title="Condition & History"
                isMobile={isMobile}
              />
              <View style={styles.specGrid}>
                <SpecRow
                  label="Mileage"
                  value={formatMileage(specs.miles || snapshot.miles)}
                  icon="speedometer"
                />
                <SpecRow
                  label="Previous Owners"
                  value={specs.owners != null ? `${specs.owners}` : null}
                  icon="account-group"
                />
                <SpecRow
                  label="Registration Date"
                  value={specs.registrationDate}
                  icon="calendar"
                />
                <SpecRow
                  label="ULEZ Compliant"
                  value={
                    specs.ulezCompliant != null
                      ? specs.ulezCompliant
                        ? 'Yes'
                        : 'No'
                      : null
                  }
                  icon="leaf"
                />
                <SpecRow
                  label="Euro Status"
                  value={specs.euroStatus}
                  icon="certificate"
                />
                <SpecRow
                  label="Insurance Group"
                  value={specs.insuranceGroup}
                  icon="shield-check"
                />
              </View>

              {/* EV Data (if applicable) */}
              {isEV && (specs.evRange || specs.evChargeTime) && (
                <>
                  <SectionHeader
                    icon="ev-station"
                    title="Electric Vehicle Data"
                    isMobile={isMobile}
                  />
                  <View style={styles.specGrid}>
                    <SpecRow
                      label="Range"
                      value={specs.evRange ? `${specs.evRange} miles` : null}
                      icon="map-marker-distance"
                    />
                    <SpecRow
                      label="Charge Time"
                      value={specs.evChargeTime}
                      icon="battery-charging"
                    />
                  </View>
                </>
              )}

              {/* Features & Options */}
              {(features.length > 0 || highValueFeatures.length > 0 || isDetailLoading) && (
                <>
                  <SectionHeader icon="format-list-checks" title="Features & Options" isMobile={isMobile} />
                  {isDetailLoading ? (
                    <ActivityIndicator
                      size="small"
                      color={COLORS.primary}
                      style={styles.loader}
                    />
                  ) : (
                    <View style={styles.featuresGrid}>
                      {highValueFeatures.map((f, i) => (
                        <FeatureChip
                          key={`hv-${i}`}
                          text={typeof f === 'string' ? f : f.name || f.feature}
                          isHighValue
                        />
                      ))}
                      {features.map((f, i) => (
                        <FeatureChip
                          key={`feat-${i}`}
                          text={typeof f === 'string' ? f : f.name || f.feature}
                          isHighValue={false}
                        />
                      ))}
                    </View>
                  )}
                </>
              )}

              {/* Similar Listings */}
              {similarListings.length > 0 && (
                <>
                  <SectionHeader
                    icon="car-multiple"
                    title="Similar Listings"
                    isMobile={isMobile}
                  />
                  {similarListings.map((sl) => (
                    <SimilarListingCard
                      key={sl.id}
                      listing={sl}
                      onPress={handleSimilarPress}
                    />
                  ))}
                </>
              )}

              {/* Seller Comments */}
              {detail?.seller_comments && (
                <>
                  <SectionHeader
                    icon="message-text-outline"
                    title="Seller Comments"
                    isMobile={isMobile}
                  />
                  <Text style={styles.sellerComments}>{detail.seller_comments}</Text>
                </>
              )}

              {/* Action Buttons */}
              <View style={[styles.actionsContainer, isMobile && styles.actionsContainerMobile]}>
                {/* View on Dealer Website CTA */}
                {!isUnavailable && snapshot.vdp_url && (
                  <Pressable
                    style={styles.financeCta}
                    onPress={() => {
                      posthog?.capture('listing_dealer_click', {
                        listing_id: feedEntry.id,
                        model_id: feedEntry.model_id,
                        vdp_url: snapshot.vdp_url,
                      });
                      Linking.openURL(snapshot.vdp_url);
                    }}
                    accessibilityRole="link"
                    accessibilityLabel="View on dealer website"
                  >
                    <Icon name="open-in-new" size={18} color={COLORS.white} />
                    <Text style={styles.financeCtaText}>View on Dealer Website</Text>
                  </Pressable>
                )}

                {/* Share Button */}
                <Pressable
                  style={styles.shareButton}
                  onPress={handleShare}
                  disabled={isSharing}
                  accessibilityRole="button"
                  accessibilityLabel="Share listing"
                >
                  {isSharing ? (
                    <ActivityIndicator size="small" color={COLORS.primary} />
                  ) : (
                    <>
                      <Icon
                        name={shareCopied ? 'check' : 'share-variant-outline'}
                        size={18}
                        color={COLORS.primary}
                      />
                      <Text style={styles.shareButtonText}>
                        {shareCopied ? 'Link Copied' : 'Share'}
                      </Text>
                    </>
                  )}
                </Pressable>
              </View>

              {/* Finance Lead Form — disabled, replaced by dealer website link */}
              {false && (
                <FinanceLeadForm
                  feedEntry={feedEntry}
                  snapshot={snapshot}
                  onSuccess={() => setShowFinanceForm(false)}
                />
              )}

              {/* Share URL display (after sharing) */}
              {shareUrl && (
                <View style={styles.shareUrlContainer}>
                  <Text style={styles.shareUrlText} numberOfLines={1} ellipsizeMode="middle">
                    {shareUrl}
                  </Text>
                </View>
              )}
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

// ============================================================
// Styles
// ============================================================

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modal: {
    width: 600,
    maxHeight: '92%',
    backgroundColor: COLORS.white,
    borderRadius: 20,
    overflow: 'hidden',
    boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.18)',
  },
  modalMobile: {
    width: '95%',
    maxWidth: 420,
    maxHeight: '95%',
    borderRadius: 16,
  },
  closeButton: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.12)',
    zIndex: 10,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 32,
  },
  body: {
    paddingHorizontal: 24,
  },
  bodyMobile: {
    paddingHorizontal: 16,
  },

  // Listing prev/next navigation
  listingNavRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.borderColor,
    backgroundColor: COLORS.lightGray,
  },
  listingNavButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 8,
  },
  listingNavDisabled: {
    opacity: 0.35,
  },
  listingNavText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 13,
    color: COLORS.textPrimary,
  },
  listingNavTextDisabled: {
    color: COLORS.mediumGray,
  },
  listingNavCounter: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 13,
    color: COLORS.textSecondary,
  },

  // Header section
  headerSection: {
    marginTop: 20,
    marginBottom: 16,
  },
  title: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 22,
    color: COLORS.textPrimary,
    lineHeight: 30,
    marginBottom: 8,
  },
  titleMobile: {
    fontSize: 18,
    lineHeight: 24,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  price: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 24,
    color: COLORS.textPrimary,
    lineHeight: 32,
  },
  priceChangeChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  priceChangeAmount: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 12,
  },
  quickInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    flexWrap: 'wrap',
  },
  quickInfoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  quickInfoText: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 13,
    color: COLORS.textSecondary,
  },

  // Score summary
  scoreSummary: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    backgroundColor: COLORS.lightGray,
    padding: 16,
    borderRadius: 14,
    borderWidth: 1,
    marginBottom: 4,
  },
  scoreCircle: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  scoreNumber: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 36,
    lineHeight: 40,
  },
  scoreOutOf: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 16,
    color: COLORS.textTertiary,
  },
  scoreInfo: {
    flex: 1,
  },
  scoreLabel: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 16,
    marginBottom: 2,
  },
  scoreDesc: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 12,
    color: COLORS.textTertiary,
    lineHeight: 16,
  },

  // Spec grid
  specGrid: {
    marginBottom: 4,
  },

  // Features
  featuresGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 4,
  },

  // Seller comments
  sellerComments: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 13,
    color: COLORS.textSecondary,
    lineHeight: 20,
    marginBottom: 4,
  },

  // Loading / Error
  loader: {
    paddingVertical: 20,
  },
  errorText: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 13,
    color: '#EF4444',
    paddingVertical: 12,
  },
  emptyText: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 13,
    color: COLORS.textTertiary,
    paddingVertical: 12,
  },

  // Actions
  actionsContainer: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 28,
    alignItems: 'center',
  },
  actionsContainerMobile: {
    flexDirection: 'column',
    gap: 10,
  },
  financeCta: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: COLORS.primary,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 12,
  },
  financeCtaText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 15,
    color: COLORS.white,
  },
  shareButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: COLORS.lightGray,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: COLORS.borderColor,
    minWidth: 120,
  },
  shareButtonText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 15,
    color: COLORS.primary,
  },

  // Share URL
  shareUrlContainer: {
    marginTop: 10,
    backgroundColor: COLORS.lightGray,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: COLORS.borderColor,
  },
  shareUrlText: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 12,
    color: COLORS.textSecondary,
  },
});

export default memo(ListingDetailPopup);
